import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity, OmschrijvingEnBedrag,
  VpbBerekening
} from '@snelstart-ventures/fiscaal/domain-logic';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  merge,
  Observable,
  shareReplay,
  switchMap,
  tap
} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormControl, FormGroup} from "@angular/forms";
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-vermogens-vergelijking',
  templateUrl: './vermogens-vergelijking.component.html',
  styleUrls: ['./vermogens-vergelijking.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    AsyncPipe
  ]
})
export class VermogensVergelijkingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte!.vermogensvergelijking) {
          const vv = data.aangifte!.vermogensvergelijking;
          if (vv.onttrekkingen?.[0]) {
            this.onttrekkingenFg.patchValue(vv.onttrekkingen[0], {emitEvent: false});
          }
          if (vv.stortingen?.[0]) {
            this.stortingenFg.patchValue(vv.stortingen[0], {emitEvent: false});
          }
        }
        if (data.aangifte!.specificatieNietAftrekbareBedragen) {
          this.nietAftrekbareBedragenFg.patchValue(data.aangifte!.specificatieNietAftrekbareBedragen, {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening$.pipe(
      filter((data): data is VpbBerekening => data != null),
      tap(data => {
        if (data.fiscaleWinst) {
          this.fiscaleWinstFg.patchValue(data.fiscaleWinst, {emitEvent: false});
        }
        if (data.jaarstukken?.resultaat) {
          this.fiscaleWinstResultatenrekeningFg.patchValue(data.jaarstukken?.resultaat, {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public stortingenFg = new FormGroup({
    omschrijving: new FormControl<string | null>(null),
    bedrag: new FormControl<number | null>(null),
  });

  public onttrekkingenFg = new FormGroup({
    omschrijving: new FormControl<string | null>(null),
    bedrag: new FormControl<number | null>(null),
  });

  public nietAftrekbareBedragenFg = new FormGroup({
    vennootschapsbelasting: new FormControl<number | null>(null),
    buitenlandseWinstbelasting: new FormControl<number | null>(null),
    nietAftrekbareUitdelingCooperatie: new FormControl<number | null>(null),
    andereUitdelingenVanWinst: new FormControl<number | null>(null),
    nietAftrekbareCommissarisbeloningen: new FormControl<number | null>(null),
    nietAftrekbareTantiemes: new FormControl<number | null>(null),
    nietAftrekbareKapitaalsvergoedingen: new FormControl<number | null>(null),
    resultaatTijdelijkIngekochteAandelen: new FormControl<number | null>(null),
    kostenFiscaleEenheidAankoopResterendeBelang: new FormControl<number | null>(null),
  });

  public fiscaleWinstFg = new FormGroup({
    eindvermogen: new FormControl<number | null>(null),
    onttrekkingen: new FormControl<number | null>(null),
    eindvermogenPlusOnttrekkingen: new FormControl<number | null>(null),
    beginvermogen: new FormControl<number | null>(null),
    stortingen: new FormControl<number | null>(null),
    beginvermogenPlusStortingen: new FormControl<number | null>(null),
    vermogensverschil: new FormControl<number | null>(null),
    nietAftrekbareBedragen: new FormControl<number | null>(null),
    saldoFiscaleWinst: new FormControl<number | null>(null),
  });
  public fiscaleWinstResultatenrekeningFg = new FormGroup({
    saldoFiscaleWinstberekening: new FormControl<number | null>(null),
  });


  ngOnInit(): void {
    merge(
      this.stortingenFg.controls.bedrag.valueChanges,
      this.onttrekkingenFg.controls.bedrag.valueChanges,
      this.nietAftrekbareBedragenFg.controls.vennootschapsbelasting.valueChanges,
      this.nietAftrekbareBedragenFg.controls.buitenlandseWinstbelasting.valueChanges,
      this.nietAftrekbareBedragenFg.controls.andereUitdelingenVanWinst.valueChanges,
    ).pipe(
      debounceTime(500),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(() => this.saveForm()),
      switchMap(() => this.dataService.calculateLatestCorporateTaxReturn()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.stortingenFg.updateValueAndValidity({ emitEvent: false });
    this.onttrekkingenFg.updateValueAndValidity({ emitEvent: false });
    this.nietAftrekbareBedragenFg.updateValueAndValidity({ emitEvent: false });

    const specificatie = this.nietAftrekbareBedragenFg.getRawValue();
    const stortingRaw = this.stortingenFg.getRawValue();
    const onttrekkingRaw = this.onttrekkingenFg.getRawValue();

    const storting: OmschrijvingEnBedrag = {
      omschrijving: stortingRaw.omschrijving?.trim()
        ? stortingRaw.omschrijving
        : undefined,
      bedrag: stortingRaw.bedrag ?? undefined,
    };

    const onttrekking: OmschrijvingEnBedrag = {
      omschrijving: onttrekkingRaw.omschrijving?.trim()
        ? onttrekkingRaw.omschrijving
        : undefined,
      bedrag: onttrekkingRaw.bedrag ?? undefined,
    };

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          vermogensvergelijking: {
            ...currentEntity.aangifte.vermogensvergelijking,
            stortingen: storting.bedrag != null || storting.omschrijving
              ? [storting]
              : [],
            onttrekkingen: onttrekking.bedrag != null || onttrekking.omschrijving
              ? [onttrekking]
              : [],
          },
          specificatieNietAftrekbareBedragen: {
            ...currentEntity.aangifte.specificatieNietAftrekbareBedragen,
            vennootschapsbelasting: specificatie.vennootschapsbelasting ?? undefined,
            buitenlandseWinstbelasting: specificatie.buitenlandseWinstbelasting ?? undefined,
            nietAftrekbareUitdelingCooperatie: specificatie.nietAftrekbareUitdelingCooperatie ?? undefined,
            andereUitdelingenVanWinst: specificatie.andereUitdelingenVanWinst ?? undefined,
            nietAftrekbareCommissarisbeloningen: specificatie.nietAftrekbareCommissarisbeloningen ?? undefined,
            nietAftrekbareTantiemes: specificatie.nietAftrekbareTantiemes ?? undefined,
            nietAftrekbareKapitaalsvergoedingen: specificatie.nietAftrekbareKapitaalsvergoedingen ?? undefined,
            resultaatTijdelijkIngekochteAandelen: specificatie.resultaatTijdelijkIngekochteAandelen ?? undefined,
            kostenFiscaleEenheidAankoopResterendeBelang: specificatie.kostenFiscaleEenheidAankoopResterendeBelang ?? undefined,
          }
        },
      };
    });
  }
}
